/* eslint-disable no-unused-vars */
/**
 * FormTemplatePreviewDialog — REAL-renderer preview for the Form Builder
 * (Jun 2026). Derived from V2NoteHost: mounts the actual NoteDetailDialog
 * with a SYNTHETIC note stamped from the builder's draft fields, so the
 * preview is pixel-identical to real view/edit modes. All persistence
 * (autosave, sign, save) is disabled — nothing can be written from here.
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { useStaticData } from '../../context/StaticDataContext';
import { useAppointments } from '../../context/AppointmentsContext';
import { useV2AppointmentDialogLegacy } from '../../hooks/useV2AppointmentDialogLegacy';
import { AppointmentScheduleDialog } from './AppointmentScheduleDialog';
import { useNoteAutosaveUI } from '../NoteAutosaveWrapper';
import LazySignaturePad from '../LazySignaturePad';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { NoteDetailDialog } from './NoteDetailDialog';
import { NoteHistoryPanel } from '../notes/NoteHistoryPanel';
import { SignatureConfirmDialog, CorrectionsDialog, NotBillableDialog } from './note-dialogs/NoteCompanionDialogs';
import { LinkGoalsDialog } from './note-dialogs/LinkGoalsDialog';
import { NextStepsDialog } from './note-dialogs/NextStepsDialog';
import { isSuperAdmin } from '../../utils/helpers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NOTE_SIZE_PRESETS = {
  split: { width: '50vw', label: 'Split', icon: 'columns' },
  medium: { width: '75vw', label: '75%', icon: 'square' },
  full: { width: '95vw', label: 'Full', icon: 'maximize' },
};

const calculateAge = (dob) => {
  if (!dob) return null;
  const b = new Date(dob);
  if (isNaN(b)) return null;
  const t = new Date();
  let a = t.getFullYear() - b.getFullYear();
  const m = t.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && t.getDate() < b.getDate())) a--;
  return a;
};
const formatDateMMDDYYYY = (s) => {
  if (!s) return '';
  const d = String(s).slice(0, 10).split('-');
  return d.length === 3 ? `${d[1]}/${d[2]}/${d[0]}` : String(s);
};
const formatLocalDateTime = (date) => {
  if (!date) return '';
  const d = new Date(date);
  return isNaN(d) ? '' : d.toLocaleString();
};
const formatTimeTo12Hour = (t) => {
  if (!t) return '';
  const [h, m] = String(t).split(':');
  if (h === undefined || m === undefined) return t;
  const hr = parseInt(h, 10);
  const ampm = hr >= 12 ? 'PM' : 'AM';
  return `${hr % 12 || 12}:${m} ${ampm}`;
};

export default function FormTemplatePreviewDialog({ onClose, templateData, fields, previewMode = 'edit', setPreviewMode }) {
  const noteId = 'preview-note';
  const forceViewMode = previewMode === 'view';
  const { user, setUser } = useAuth();
  const staticData = useStaticData();
  const {
    providers = [], locations = [], posCodes = [], users = [],
    organizations = [], cptCodes = [], formTemplates = [], serviceTypes = [],
  } = staticData || {};

  // ── Core data ──────────────────────────────────────────────────────
  const [selectedNote, setSelectedNote] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientNotes, setClientNotes] = useState([]);
  const [clientAppointments, setClientAppointments] = useState([]);
  const [clientDiagnoses, setClientDiagnoses] = useState([]);
  const [clientMedications, setClientMedications] = useState([]);
  const [clientVitals, setClientVitals] = useState([]);
  const [clientGroups] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fallbackUsers, setFallbackUsers] = useState([]);
  const effectiveUsers = users && users.length ? users : fallbackUsers;
  const [fallbackTemplates, setFallbackTemplates] = useState([]);
  const effectiveTemplates = formTemplates && formTemplates.length ? formTemplates : fallbackTemplates;

  // StaticDataContext is populated by the legacy dashboard on login;
  // when landing directly on /v2/note it can be empty — fetch fallbacks
  // so provider names resolve and template fields render.
  useEffect(() => {
    if (users && users.length) return;
    let cancelled = false;
    axios.get(`${API}/users`).then(r => {
      if (!cancelled) setFallbackUsers(Array.isArray(r.data) ? r.data : (r.data?.users || []));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [users]);
  useEffect(() => {
    if (formTemplates && formTemplates.length) return;
    let cancelled = false;
    axios.get(`${API}/form-templates`).then(r => {
      if (!cancelled) setFallbackTemplates(Array.isArray(r.data) ? r.data : (r.data?.templates || []));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [formTemplates]);

  // ── Editing state ──────────────────────────────────────────────────
  const [showNoteDetail, setShowNoteDetail] = useState(true);
  const [isEditingNote, setIsEditingNote] = useState(false);
  const [editedNoteContent, setEditedNoteContent] = useState({});
  const [editedNoteConsent, setEditedNoteConsent] = useState(null);
  const [linkedGoals, setLinkedGoals] = useState([]);
  const [isSavingNote, setIsSavingNote] = useState(false);
  const [autosaveBlockedReason, setAutosaveBlockedReason] = useState(null);
  const [noteCreationError, setNoteCreationError] = useState(null);
  const [showCloseEditingNoteWarning, setShowCloseEditingNoteWarning] = useState(false);
  const [loadedSignatures, setLoadedSignatures] = useState({});
  const [linkedFormsAdded, setLinkedFormsAdded] = useState([]);
  const [noteLinkedAppointment, setNoteLinkedAppointment] = useState(null);
  const [noteJustSigned, setNoteJustSigned] = useState(false);
  const [noteDialogSize, setNoteDialogSize] = useState('full');

  // ── Companion dialog state ─────────────────────────────────────────
  const [showSignatureConfirm, setShowSignatureConfirm] = useState(false);
  const [signatureAction, setSignatureAction] = useState('provider');
  const [signatureDate, setSignatureDate] = useState(new Date().toISOString());
  const [signWithoutDate, setSignWithoutDate] = useState(false);
  const [showCorrectionsDialog, setShowCorrectionsDialog] = useState(false);
  const [correctionsReason, setCorrectionsReason] = useState('');
  const [showNotBillableDialog, setShowNotBillableDialog] = useState(false);
  const [notBillableTargetNote, setNotBillableTargetNote] = useState(null);
  const [notBillableReason, setNotBillableReason] = useState('');
  const [notBillableComment, setNotBillableComment] = useState('');
  const [showLinkGoalsDialog, setShowLinkGoalsDialog] = useState(false);
  const [availableTreatmentPlanGoals, setAvailableTreatmentPlanGoals] = useState([]);
  const [loadingTreatmentPlanGoals, setLoadingTreatmentPlanGoals] = useState(false);
  const [showNextStepsDialog, setShowNextStepsDialog] = useState(false);
  const [showNoteHistory, setShowNoteHistory] = useState(false);
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const [pendingSignType, setPendingSignType] = useState('provider');
  const [savingSignature, setSavingSignature] = useState(false);
  const sigPadRef = useRef(null);

  const closeHost = useCallback(() => { if (onClose) onClose(); }, [onClose]);

  // ── Synthetic preview note (no server round-trip) ──────────────────
  useEffect(() => {
    // Mirror real note creation: fall back to the field's default_value
    // (incl. 'today'/'now' specials) so builder defaults show in preview.
    const stamped = (fields || []).map(f => {
      let v = (f.value !== undefined && f.value !== '' && f.value !== null) ? f.value : f.default_value;
      if (f.type === 'date' && v === 'today') v = new Date().toISOString().split('T')[0];
      if (f.type === 'time' && v === 'now') v = new Date().toTimeString().slice(0, 5);
      return { ...f, value: (v === undefined || v === null) ? '' : v };
    });
    const content = {
      template_id: 'preview-template',
      template_name: templateData?.name || 'Template Preview',
      fields: stamped,
    };
    const nowIso = new Date().toISOString();
    const note = {
      id: 'preview-note',
      client_id: 'preview-client',
      provider_id: user?.id,
      status: forceViewMode ? 'completed' : 'draft',
      title: templateData?.name || 'Template Preview',
      template_id: 'preview-template',
      template_name: templateData?.name || 'Template Preview',
      content: JSON.stringify(content),
      date_of_service: nowIso.slice(0, 10),
      created_at: nowIso,
      updated_at: nowIso,
      client_name: 'Sample Client',
      doc_number: 'PREVIEW',
      signatures_required: [],
    };
    setSelectedNote(note);
    setEditedNoteContent(content);
    setLinkedGoals([]);
    setIsEditingNote(!forceViewMode);
    setEditedNoteConsent(!forceViewMode
      ? { provider_location: null, pos_code: null, pos_description: null, modality: null }
      : null);
    setSelectedClient({
      id: 'preview-client',
      first_name: 'Sample', last_name: 'Client', full_name: 'Sample Client',
      date_of_birth: '1990-01-01', mrn: 'MRN-PREVIEW', status: 'active',
      email: 'sample.client@example.com', phone: '(555) 555-0100',
    });
    setLoading(false);

  }, [fields, templateData?.name, forceViewMode, user?.id]);

  // ── Autosave (shared engine) ───────────────────────────────────────
  const autosaveEnabled = false; // PREVIEW: persistence disabled
  const noteEditingAutosave = useNoteAutosaveUI({
    noteId: selectedNote?.id || null,
    setNoteId: () => {},
    formData: editedNoteContent,
    consentData: editedNoteConsent,
    linkedGoals,
    metadata: selectedNote ? {
      date_of_service: selectedNote.date_of_service,
      start_time: selectedNote.start_time,
      end_time: selectedNote.end_time,
      units: selectedNote.units,
      cpt_code: selectedNote.cpt_code,
      pos_code: selectedNote.pos_code,
      modality: selectedNote.modality,
      ready_for_billing: selectedNote.ready_for_billing,
    } : null,
    enabled: autosaveEnabled,
    initialVersion: selectedNote?.current_version || null,
    serverUpdatedAt: selectedNote?.updated_at || null,
    createNoteData: null,
    onNoteCreated: () => {},
    onSaveError: setAutosaveBlockedReason,
    onSaveCriticalError: setNoteCreationError,
    clientName: selectedNote?.client_name || null,
    providerName: effectiveUsers?.find(u => u.id === selectedNote?.provider_id)?.full_name || null,
    clientId: selectedNote?.client_id || null,
    onFormDataRestore: (restored) => {
      if (restored.formData) setEditedNoteContent(restored.formData);
      if (restored.consentData !== undefined) setEditedNoteConsent(restored.consentData);
      if (restored.linkedGoals) setLinkedGoals(restored.linkedGoals);
    },
  });

  // ── Shared helpers ─────────────────────────────────────────────────
  const refreshNotes = useCallback(async () => {}, []);
  const refreshTodos = useCallback(() => {}, []);
  const refreshClientNotes = useCallback(async () => {
    if (!selectedNote?.client_id) return;
    try {
      const { data } = await axios.get(`${API}/notes?client_id=${selectedNote.client_id}&limit=100`);
      setClientNotes(Array.isArray(data) ? data : (data?.notes || []));
    } catch { /* noop */ }
  }, [selectedNote?.client_id]);

  const updateNoteStatusOptimistically = useCallback((id, status, extra = {}) => {
    setSelectedNote(prev => (prev && prev.id === id ? { ...prev, status, ...extra } : prev));
    setClientNotes(prev => prev.map(n => (n.id === id ? { ...n, status, ...extra } : n)));
  }, []);

  const userRoleName = (user?.role || '').toLowerCase();
  const canEditNote = useCallback((note) => {
    if (!user || !note) return false;
    if (isSuperAdmin(user)) return true;
    if (['locked', 'billed', 'paid'].includes(note.status)) return false;
    return note.provider_id === user.id || note.assigned_supervisor === user.id
      || note.assigned_supervisor_2 === user.id
      || ['office', 'office staff', 'biller'].includes(userRoleName);
  }, [user, userRoleName]);
  const canLockNote = useCallback((note) => {
    if (!user || !note) return false;
    if (isSuperAdmin(user)) return true;
    const perms = (user.permissions || user.role_permissions || user.effective_permissions || []);
    return ['office', 'office staff', 'biller'].includes(userRoleName)
      || (Array.isArray(perms) && perms.includes('lock_unlock_notes'));
  }, [user, userRoleName]);

  const openSignatureConfirm = useCallback((type) => {
    setSignatureAction(type === 'supervisor_2' ? 'supervisor' : type);
    setSignatureDate(new Date().toISOString());
    setSignWithoutDate(false);
    setShowSignatureConfirm(true);
  }, []);

  const handleSignNote = useCallback((type) => {
    toast.info('Preview only — signing is disabled');
    return;
    // eslint-disable-next-line no-unreachable
    if (!user?.signature_image) {
      // Native V2 signature pad: draw + save to profile, then continue.
      setPendingSignType(type);
      setShowSignaturePad(true);
      return;
    }
    openSignatureConfirm(type);
  }, [user, openSignatureConfirm]);

  const handleSaveSignatureAndContinue = useCallback(async () => {
    if (!sigPadRef.current || sigPadRef.current.isEmpty()) {
      toast.error('Please provide a signature first');
      return;
    }
    setSavingSignature(true);
    try {
      const signatureImage = sigPadRef.current.toDataURL();
      await axios.put(`${API}/users/me/signature`, { signature_image: signatureImage });
      setUser(prev => (prev ? { ...prev, signature_image: signatureImage } : prev));
      toast.success('Signature saved to your profile');
      setShowSignaturePad(false);
      openSignatureConfirm(pendingSignType);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save signature to your profile');
    } finally {
      setSavingSignature(false);
    }
  }, [pendingSignType, openSignatureConfirm, setUser]);

  const handleUnsignNote = useCallback(async (type) => {
    toast.info('Preview only — signing is disabled');
    return;
    // eslint-disable-next-line no-unreachable
    try {
      await axios.put(`${API}/notes/${noteId}`, {
        [`${type}_signed_at`]: null,
        [`${type}_signed_by`]: null,
      });
      toast.success('Signature removed');
      refreshNotes();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove signature');
    }
  }, [noteId, refreshNotes]);

  const fetchTreatmentPlanGoals = useCallback(async (clientId) => {
    setLoadingTreatmentPlanGoals(true);
    try {
      const { data } = await axios.get(`${API}/clients/${clientId}/treatment-plan-goals`);
      setAvailableTreatmentPlanGoals(data?.plans || data || []);
    } catch {
      setAvailableTreatmentPlanGoals([]);
    } finally {
      setLoadingTreatmentPlanGoals(false);
    }
  }, []);

  const getSignatureUrl = useCallback(async (userId) => {
    try {
      const { data } = await axios.get(`${API}/users/${userId}/signature`);
      return data?.signature_image || null;
    } catch { return null; }
  }, []);

  const replaceClientVariables = useCallback((text, clientData) => {
    if (!text || !clientData) return text;
    return String(text)
      .replace(/\{client_name\}/gi, `${clientData.first_name || ''} ${clientData.last_name || ''}`.trim())
      .replace(/\{first_name\}/gi, clientData.first_name || '')
      .replace(/\{last_name\}/gi, clientData.last_name || '');
  }, []);

  const openNote = useCallback(() => {}, []);
  const handleNavigateToNote = openNote;

  const closeNoteWithCheck = useCallback(() => {
    const hasReal = noteEditingAutosave?.hasRealUnsavedChanges?.() ?? false;
    if (isEditingNote && hasReal) { setShowCloseEditingNoteWarning(true); return; }
    closeHost();
  }, [noteEditingAutosave, isEditingNote, closeHost]);
  const confirmAction = useCallback((message, onConfirm) => {
    if (window.confirm(typeof message === 'string' ? message : 'Are you sure?')) onConfirm?.();
  }, []);

  // Write a field's value into the STRUCTURED note content (fields/sections)
  // so the renderer re-derives values and autosave serializes correctly.
  const applyFieldValues = useCallback((valueMap) => {
    setEditedNoteContent(prev => {
      if (!prev || typeof prev !== 'object') return prev;
      const setVal = (f) => (f.id && Object.prototype.hasOwnProperty.call(valueMap, f.id))
        ? { ...f, value: valueMap[f.id] } : f;
      if (Array.isArray(prev.fields)) {
        return { ...prev, fields: prev.fields.map(setVal) };
      }
      if (Array.isArray(prev.sections)) {
        return { ...prev, sections: prev.sections.map(s => ({ ...s, fields: (s.fields || []).map(setVal) })) };
      }
      return { ...prev, ...valueMap };
    });
  }, []);

  const handleFieldChangeLifted = useCallback((fieldId, value) => {
    applyFieldValues({ [fieldId]: value });
  }, [applyFieldValues]);

  // Bridge for renderers that call setNewNoteFormData(prev => next) — basic
  // fields (text/checkbox/radio/select/file) route through this. Was a noop
  // (Jun 2026 fix): every basic-field edit in the V2 editor was silently
  // dropped (e.g. uncheckable checkboxes).
  const setNewNoteFormDataBridge = useCallback((updater) => {
    if (typeof updater !== 'function') return;
    setEditedNoteContent(prev => {
      if (!prev || typeof prev !== 'object') return prev;
      const current = {};
      if (Array.isArray(prev.fields)) {
        prev.fields.forEach(f => { if (f.id) current[f.id] = f.value; });
      } else if (Array.isArray(prev.sections)) {
        prev.sections.forEach(s => (s.fields || []).forEach(f => { if (f.id) current[f.id] = f.value; }));
      }
      const nextData = updater(current) || {};
      const changed = {};
      Object.keys(nextData).forEach(k => { if (nextData[k] !== current[k]) changed[k] = nextData[k]; });
      if (Object.keys(changed).length === 0) return prev;
      const setVal = (f) => (f.id && Object.prototype.hasOwnProperty.call(changed, f.id))
        ? { ...f, value: changed[f.id] } : f;
      if (Array.isArray(prev.fields)) {
        return { ...prev, fields: prev.fields.map(setVal) };
      }
      if (Array.isArray(prev.sections)) {
        return { ...prev, sections: prev.sections.map(s => ({ ...s, fields: (s.fields || []).map(setVal) })) };
      }
      return { ...prev, ...changed };
    });
  }, []);

  const legacyFallback = useCallback((label) => () => {
    toast.info(`${label} is disabled in template preview`);
  }, []);
  const noop = useCallback(() => {}, []);

  // ── Native appointment dialog (Jun 2026 — "Open Appt" from the note
  // footer was a no-op in V2).  Wire the AppointmentsContext setters the
  // shared NoteDetailDialog calls, and mount AppointmentScheduleDialog
  // below (same pattern as ClientChartV2Dialog Phase 2H).
  const {
    setShowAppointmentForm: apptSetShowForm,
    setEditingAppointment: apptSetEditing,
    setAppointmentFormData: apptSetFormData,
  } = useAppointments();
  const refreshClientAppointments = useCallback(async () => {
    const cid = null; // PREVIEW: no client appointment fetches
    if (!cid) return;
    try {
      const r = await axios.get(`${API}/appointments?client_id=${cid}`);
      const appts = Array.isArray(r.data) ? r.data : (r.data?.appointments || []);
      setClientAppointments(appts);
    } catch { /* non-fatal */ }
  }, [selectedNote?.client_id, selectedClient?.id]);
  const v2ApptLegacy = useV2AppointmentDialogLegacy({
    refreshAppointments: refreshClientAppointments,
  });
  // Legacy "Open Appointment" closes the note + switches to the calendar
  // tab; in V2 that would unmount this host mid-flight.  Instead, keep the
  // note open underneath and let the appointment dialog stack on top.
  const apptOpeningRef = useRef(false);
  const guardedSetEditingAppointment = useCallback((apt) => {
    if (apt) apptOpeningRef.current = true;
    apptSetEditing(apt);
  }, [apptSetEditing]);
  const guardedSetShowNoteDetail = useCallback((v) => {
    if (v === false && apptOpeningRef.current) {
      apptOpeningRef.current = false;
      return;
    }
    setShowNoteDetail(v);
  }, []);

  // close-warning handlers (shared save engine)
  const handleCloseEditingNoteSaveAndClose = useCallback(async () => {
    try {
      await noteEditingAutosave?.forceSave?.();
    } catch { /* surfaced by engine */ }
    setShowCloseEditingNoteWarning(false);
    closeHost();
  }, [noteEditingAutosave, closeHost]);
  const handleCloseEditingNoteDiscard = useCallback(() => {
    setShowCloseEditingNoteWarning(false);
    closeHost();
  }, [closeHost]);
  const handleCloseEditingNoteKeepEditing = useCallback(() => setShowCloseEditingNoteWarning(false), []);

  const getAccessibleCPTCodes = useCallback(() => cptCodes, [cptCodes]);
  const getSchedulersForProvider = useCallback(() => [], []);

  // When the host dialog is dismissed, navigate back to V2.
  useEffect(() => { if (!showNoteDetail) closeHost(); }, [showNoteDetail, closeHost]);

  const ctx = useMemo(() => ({
    API,
    NOTE_SIZE_PRESETS,
    allClientsForLookup: selectedClient ? [selectedClient] : [],
    appointments: clientAppointments,
    autosaveBlockedReason,
    availablePANs: [],
    availableTreatmentPlanGoals,
    calculateAge,
    canEditNote,
    canLockNote,
    clientAppointments,
    clientDiagnoses,
    clientGroups,
    clientMedications,
    clientMismatchInfo: null,
    clientNotes,
    clientVitals,
    clients: selectedClient ? [selectedClient] : [],
    closeNoteWithCheck,
    confirmAction,
    cptCodes,
    editContextMenuPos: { x: 0, y: 0 },
    editedNoteConsent,
    editedNoteContent,
    editingNote: isEditingNote ? selectedNote : null,
    fetchAppointments: refreshClientAppointments,
    fetchTreatmentPlanGoals,
    formTemplates: effectiveTemplates,
    formatDateMMDDYYYY,
    formatLocalDateTime,
    formatTimeTo12Hour,
    getAccessibleCPTCodes,
    getSignatureUrl,
    handleAddLinkedForm: legacyFallback('Adding linked forms'),
    handleCloseEditingNoteDiscard,
    handleCloseEditingNoteKeepEditing,
    handleCloseEditingNoteSaveAndClose,
    handleFieldChangeLifted,
    handleMismatchKeepEditing: noop,
    handleMismatchMoveNote: noop,
    handleMismatchSaveAndClose: noop,
    handleNavigateToNote,
    handleSignNote,
    handleUnsignNote,
    isEditingNote,
    isSavingNote,
    linkedFormsAdded,
    linkedGoals,
    loadedSignatures,
    locations,
    noteCreationError,
    noteDialogSize,
    noteEditingAutosave,
    noteHasClientVisibilityDivider: () => false,
    noteJustSigned,
    noteLinkedAppointment,
    notes: clientNotes,
    openNote,
    organizations,
    posCodes,
    providers,
    refreshClientNotes,
    refreshNotes,
    refreshTodos,
    replaceClientVariables,
    returnToCreateNote: null,
    selectedClient,
    selectedNote,
    serviceTypes,
    setActiveTab: noop,
    setAppointmentFormData: apptSetFormData,
    setAppointments: setClientAppointments,
    setAutosaveBlockedReason,
    setClientMedications,
    setClientNotes,
    setCorrectionsReason,
    setEditContextMenuPos: noop,
    setEditedNoteConsent,
    setEditedNoteContent,
    setEditingAppointment: guardedSetEditingAppointment,
    setEditingMedication: noop,
    setIsEditingNote,
    setIsSavingNote,
    setLinkedFormsAdded,
    setLinkedGoals,
    setLoadedSignatures,
    setNewNoteFormData: noop,
    setNewNoteFormDataBridge,
    setNotBillableComment,
    setNotBillableReason,
    setNotBillableTargetNote,
    setNoteCreationError,
    setNoteJustSigned,
    setNoteLinkedAppointment,
    setNotes: setClientNotes,
    setReturnToCreateNote: noop,
    setSelectedClient,
    setSelectedNote,
    setSelectedNoteClients: noop,
    setSelectedNoteProvider: noop,
    setSelectedNoteStatus: noop,
    setSelectedTemplate: noop,
    setSendToClientNote: noop,
    setShowAddNote: legacyFallback('Creating a new note'),
    setShowAllDiagnoses: noop,
    setShowAppointmentForm: apptSetShowForm,
    setShowCloseEditingNoteWarning,
    setShowCorrectionsDialog,
    setShowDiagnosisForm: noop,
    setShowEditContextMenu: noop,
    setShowLinkGoalsDialog,
    setShowMedicationForm: noop,
    setShowNextStepsDialog,
    setShowNotBillableDialog,
    setShowNoteDetail: guardedSetShowNoteDetail,
    setShowNoteHistory,
    setShowReferencePanel: noop,
    setShowSendToClientDialog: legacyFallback('Send to client'),
    setShowSignatureContextMenu: noop,
    setSignatureContextMenuPos: noop,
    setSignatureContextMenuType: noop,
    showAllDiagnoses: false,
    showCloseEditingNoteWarning,
    showEditContextMenu: false,
    showNoteDetail,
    showReferencePanel: false,
    showSignatureContextMenu: false,
    signatureContextMenuPos: { x: 0, y: 0 },
    signatureContextMenuType: null,
    updateNoteDialogSize: setNoteDialogSize,
    updateNoteStatusOptimistically,
    user,
    userRoleName,
    users: effectiveUsers,
  }), [
    selectedClient, clientAppointments, autosaveBlockedReason, availableTreatmentPlanGoals,
    canEditNote, canLockNote, clientDiagnoses, clientGroups, clientMedications, clientNotes,
    clientVitals, closeNoteWithCheck, confirmAction, cptCodes, editedNoteConsent,
    editedNoteContent, isEditingNote, selectedNote, fetchTreatmentPlanGoals, formTemplates, effectiveTemplates,
    getAccessibleCPTCodes, getSignatureUrl, legacyFallback, handleCloseEditingNoteDiscard,
    handleCloseEditingNoteKeepEditing, handleCloseEditingNoteSaveAndClose, handleFieldChangeLifted,
    handleNavigateToNote, handleSignNote, handleUnsignNote, isSavingNote, linkedFormsAdded,
    linkedGoals, loadedSignatures, locations, noteCreationError, noteDialogSize,
    noteEditingAutosave, noteJustSigned, noteLinkedAppointment, openNote, organizations,
    posCodes, providers, refreshClientNotes, refreshNotes, refreshTodos, replaceClientVariables,
    serviceTypes, showCloseEditingNoteWarning, showNoteDetail, updateNoteStatusOptimistically,
    user, userRoleName, users, effectiveUsers, noop,
    apptSetFormData, guardedSetEditingAppointment, apptSetShowForm, refreshClientAppointments, guardedSetShowNoteDetail,
  ]);

  if (loading) {
    return (
      <div className="hidden" data-testid="v2-note-host-loading">
        <div className="text-gray-500">Loading note…</div>
      </div>
    );
  }
  if (loadError || !selectedNote) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 bg-gray-50" data-testid="v2-note-host-error">
        <p className="text-gray-700">{loadError || 'Note not found'}</p>
        <button className="text-teal-700 underline" onClick={closeHost} data-testid="v2-note-host-back-btn">Back</button>
      </div>
    );
  }

  return (
    <div data-testid="form-template-preview-host">
      {/* Floating preview banner — sits above the note dialog (z-50) */}
      <div className="fixed top-2 left-1/2 -translate-x-1/2 z-[70] pointer-events-auto flex items-center gap-3 bg-slate-900 text-white rounded-full shadow-lg px-4 py-2" style={{ pointerEvents: 'auto' }} data-testid="template-preview-banner">
        <span className="text-xs font-semibold tracking-wide uppercase">Template Preview — nothing is saved</span>
        <div className="flex items-center rounded-full bg-slate-700 p-0.5">
          <button
            type="button"
            onClick={() => setPreviewMode && setPreviewMode('edit')}
            className={`text-xs px-3 py-1 rounded-full transition-colors ${previewMode === 'edit' ? 'bg-teal-500 text-white' : 'text-slate-300 hover:text-white'}`}
            data-testid="template-preview-mode-edit"
          >
            Edit mode
          </button>
          <button
            type="button"
            onClick={() => setPreviewMode && setPreviewMode('view')}
            className={`text-xs px-3 py-1 rounded-full transition-colors ${previewMode === 'view' ? 'bg-teal-500 text-white' : 'text-slate-300 hover:text-white'}`}
            data-testid="template-preview-mode-view"
          >
            View mode
          </button>
        </div>
        <button type="button" onClick={() => onClose && onClose()} className="text-xs text-slate-300 hover:text-white underline" data-testid="template-preview-close">
          Close preview
        </button>
      </div>
      <NoteDetailDialog ctx={ctx} />
      {/* Native appointment dialog — serves the note footer "Open Appt" button (Jun 2026) */}
      <AppointmentScheduleDialog legacy={v2ApptLegacy} />
      <SignatureConfirmDialog ctx={{
        API, refreshNotes, refreshTodos, selectedNote, setShowNoteDetail,
        setShowSignatureConfirm, setSignWithoutDate, setSignatureDate,
        showSignatureConfirm, signWithoutDate, signatureAction, signatureDate,
        updateNoteStatusOptimistically, user,
      }} />
      <CorrectionsDialog ctx={{
        API, correctionsReason, refreshNotes, refreshTodos, selectedNote,
        setCorrectionsReason, setShowCorrectionsDialog, setShowNoteDetail,
        showCorrectionsDialog, user,
      }} />
      <NotBillableDialog ctx={{
        API, notBillableComment, notBillableReason, notBillableTargetNote,
        noteReviewData: null, refreshNotes, refreshTodos, setNotBillableComment,
        setNotBillableReason, setNotBillableTargetNote, setShowNotBillableDialog,
        setShowNoteReviewDialog: noop, showNotBillableDialog, updateNoteStatusOptimistically,
      }} />
      <LinkGoalsDialog ctx={{
        availableTreatmentPlanGoals, linkedGoals, loadingTreatmentPlanGoals,
        setLinkedGoals, setSelectedNote, setShowLinkGoalsDialog, showLinkGoalsDialog,
      }} />
      <NextStepsDialog ctx={{
        API, formTemplates: effectiveTemplates, getSchedulersForProvider, nextStepsCptBlocked: false,
        refreshNotes, refreshTodos, selectedNote, setNotBillableComment,
        setNotBillableReason, setNotBillableTargetNote, setScheduleNextData: noop,
        setSelectedNote, setShowNextStepsDialog, setShowNotBillableDialog,
        setShowNoteDetail, setShowScheduleNextDialog: noop, showNextStepsDialog,
        updateNoteStatusOptimistically, user,
      }} />
      <NoteHistoryPanel
        noteId={selectedNote?.id}
        isOpen={showNoteHistory}
        onClose={() => setShowNoteHistory(false)}
        onRestoreComplete={() => {
          refreshNotes();
          refreshClientNotes();
        }}
        isNoteLocked={selectedNote?.status === 'locked' || selectedNote?.status === 'signed' || selectedNote?.is_locked}
      />
      <Dialog open={showSignaturePad} onOpenChange={setShowSignaturePad}>
        <DialogContent className="max-w-lg" data-testid="v2-signature-pad-dialog">
          <DialogHeader>
            <DialogTitle>Create Your Signature</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            Draw your signature below. It will be saved to your profile and applied when you sign this note.
          </p>
          <div className="border-2 border-dashed border-gray-300 rounded-lg bg-white">
            <LazySignaturePad
              ref={sigPadRef}
              penColor="black"
              canvasProps={{ width: 460, height: 160, className: 'w-full rounded-lg', 'data-testid': 'v2-signature-pad-canvas' }}
            />
          </div>
          <div className="flex justify-between gap-2">
            <Button variant="ghost" onClick={() => sigPadRef.current?.clear()} data-testid="v2-signature-pad-clear-btn">
              Clear
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowSignaturePad(false)} data-testid="v2-signature-pad-cancel-btn">
                Cancel
              </Button>
              <Button
                className="bg-teal-600 hover:bg-teal-700 text-white"
                disabled={savingSignature}
                onClick={handleSaveSignatureAndContinue}
                data-testid="v2-signature-pad-save-btn"
              >
                {savingSignature ? 'Saving…' : 'Save & Sign Note'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
